import React, { useState, useRef, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import './AutocompleteSelect.css';

/**
 * Text input with dropdown: type to filter options, click to select.
 * @param {string} value - Current value
 * @param {function} onChange - Called with synthetic event { target: { name, value } }
 * @param {string[]} options - List of option strings
 * @param {string} name - Input name for form
 * @param {string} placeholder - Placeholder text
 * @param {boolean} disabled - Disable input
 * @param {string} id - Optional id
 * @param {boolean} required - HTML required
 */
const AutocompleteSelect = ({
  value = '',
  onChange,
  options = [],
  name,
  placeholder = 'Type or select...',
  disabled = false,
  id,
  required = false,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  const normalizedOptions = Array.isArray(options) ? options : [];
  const searchLower = (inputValue || '').trim().toLowerCase();
  const filteredOptions = searchLower
    ? normalizedOptions.filter((opt) => (opt || '').toLowerCase().includes(searchLower))
    : normalizedOptions;

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (!open) setHighlightIndex(-1);
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (open && listRef.current && highlightIndex >= 0 && highlightIndex < filteredOptions.length) {
      const el = listRef.current.children[highlightIndex];
      if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [highlightIndex, open, filteredOptions.length]);

  const handleInputChange = (e) => {
    const v = e.target.value;
    setInputValue(v);
    setOpen(true);
    setHighlightIndex(-1);
    onChange({ target: { name, value: v } });
  };

  const handleSelect = (option) => {
    setInputValue(option);
    setOpen(false);
    setHighlightIndex(-1);
    onChange({ target: { name, value: option } });
  };

  const handleFocus = () => {
    if (!disabled) setOpen(true);
  };

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        setOpen(true);
        setHighlightIndex(0);
      }
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
      setHighlightIndex(-1);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => (i < filteredOptions.length - 1 ? i + 1 : i));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => (i > 0 ? i - 1 : -1));
      return;
    }
    if (e.key === 'Enter' && highlightIndex >= 0 && filteredOptions[highlightIndex]) {
      e.preventDefault();
      handleSelect(filteredOptions[highlightIndex]);
    }
  };

  return (
    <div className="autocomplete-select" ref={containerRef}>
      <Form.Control
        type="text"
        name={name}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        id={id}
        required={required}
        autoComplete="off"
        className="autocomplete-select-input"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-haspopup="listbox"
      />
      {open && !disabled && (
        <ul
          ref={listRef}
          className="autocomplete-select-dropdown"
          role="listbox"
        >
          {filteredOptions.length === 0 ? (
            <li className="autocomplete-select-item autocomplete-select-item-empty">
              No matches
            </li>
          ) : (
            filteredOptions.slice(0, 200).map((option, index) => (
              <li
                key={option}
                role="option"
                aria-selected={value === option}
                className={`autocomplete-select-item ${index === highlightIndex ? 'highlight' : ''}`}
                onMouseEnter={() => setHighlightIndex(index)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(option);
                }}
              >
                {option}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

export default AutocompleteSelect;
